"use client";

import { TranslationData } from "@/common/lib/types";
import { cn } from "@/lib/tailwind";
import { getFieldValue, getLangFromLocale } from "@/utils/common";
import { getTextLength, truncateHtmlContent, processCmsText } from "@/utils/textProcessing";
import { useParams } from "next/navigation";
import React, { useEffect, useState } from "react";
import root from "react-shadow";

interface ITextTruncateProps
  extends Omit<
    React.DetailedHTMLProps<React.HTMLAttributes<HTMLParagraphElement>, HTMLParagraphElement>,
    "children"
  > {
  text: string;
  truncateProps?: {
    offset?: number;
    className?: string;
    customRenderer?: (toggle: () => void) => React.JSX.Element;
  };
  translations?: TranslationData;
}

const DEFAULT_TRUNCATE_OFFSET = 180;

const TextTruncate = ({
  text,
  className,
  translations,
  truncateProps,
  setTextAsInnerHTML = false,
  ...delegated
}: ITextTruncateProps & { setTextAsInnerHTML?: boolean }) => {
  // console.log(text.length);

  // consts
  const params = useParams();
  const lang = getLangFromLocale(params?.locale as string);
  
  // Process the text to handle special characters properly
  const processedText = processCmsText(text || "");
  
  // Get actual text length without HTML tags for accurate truncation
  const textLength = getTextLength(processedText);
  const isOffsetLower = textLength > (truncateProps?.offset || DEFAULT_TRUNCATE_OFFSET);

  // states
  const [readMore, setReadMore] = useState(isOffsetLower);
  const [innerHtml, setInnerHtml] = useState("");

  // methods
  const handleReadMoreToggle = () => {
    setReadMore((state) => !state);
  };

  // lifecycle
  useEffect(() => {
    let t = "";
    if (readMore && isOffsetLower) {
      const offset = truncateProps?.offset || DEFAULT_TRUNCATE_OFFSET;
      t = truncateHtmlContent(processedText, offset) + "...";
    } else {
      t = processedText;
    }

    setInnerHtml(t);
  }, [readMore, processedText, truncateProps?.offset, isOffsetLower]);

  return (
    <p
      {...delegated}
      className={cn("text-center text-[0.875rem] text-tertiary-6 md:text-[1.125rem]", className)}
    >
      <root.span>
        <style>
          {`
            p {
                margin: 0;
                padding: 0;
                font-size: inherit;
              }
            span {
                font-size: inherit;
              }
            a {
                color: #666666;
              }
          `}
        </style>
        <span dangerouslySetInnerHTML={{ __html: innerHtml }}></span>
      </root.span>

      {isOffsetLower &&
        (truncateProps?.customRenderer ? (
          truncateProps?.customRenderer(handleReadMoreToggle)
        ) : (
          <span
            onClick={handleReadMoreToggle}
            className={cn(
              "ml-1 cursor-pointer text-[0.875rem] font-semibold text-tertiary-6 underline underline-offset-2 md:ml-[2px] md:text-[1.125rem]",
              truncateProps?.className,
            )}
          >
            {readMore
              ? getFieldValue(lang, "Read More", translations?.common?.common_read_more_label)
              : getFieldValue(lang, "Show Less", translations?.common?.common_show_less_label)}
          </span>
        ))}
    </p>
  );
};

export default TextTruncate;
